import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSearchStore } from '../store/searchStore';
import { useUsageStore } from '../store/usageStore';
import api from '../services/api';
import Header from '../components/Header';
import BackToDashboard from '../components/BackToDashboard';
import UpgradePrompt from '../components/Subscription/UpgradePrompt';

interface Business {
  id: number;
  name: string;
  address: string | null;
  phone: string | null;
  website: string | null;
  rating: number | null;
  review_count: number;
  has_website: boolean;
}

interface WebsiteAnalysis {
  business_id: number;
  seo_score: number;
  has_website: boolean;
  issues: Array<{ type: string; message: string }>;
  recommendations: string[];
  summary: string | null;
}

interface LocalSEOAnalysis {
  business_id: number;
  overall_score: number;
  opportunity_score?: number;
  scores: {
    gbp?: number;
    website?: number;
    reviews?: number;
    citations?: number;
    relevance?: number;
  };
  issues: string[];
  recommendations: string[];
  summary: string;
}

function themeToStr(x: unknown): string {
  if (typeof x === 'string') return x;
  if (x != null && typeof x === 'object' && !Array.isArray(x)) {
    const o = x as Record<string, unknown>;
    const v = o.theme ?? o.example;
    if (v != null) return String(v);
  }
  return String(x ?? '');
}

const GAP_LABELS: Record<string, string> = {
  no_owner_responses: 'No responses to customer reviews',
  low_count: 'Low review count',
  low_rating: 'Low rating',
  stale_reviews: 'No recent reviews (within 90 days)',
};

function gapToLabel(gap: string): string {
  return GAP_LABELS[gap] ?? gap;
}

const BusinessDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasSearched } = useSearchStore();
  const { usage, fetchUsage } = useUsageStore();

  const hasAnalysisFeature = (feature: string) =>
    (usage?.analysis_features ?? []).includes(feature);
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [analysis, setAnalysis] = useState<WebsiteAnalysis | null>(null);
  const [localSEO, setLocalSEO] = useState<LocalSEOAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingLocalSEO, setLoadingLocalSEO] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [pitch, setPitch] = useState<string>('');
  const [generatingPitch, setGeneratingPitch] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [exportingPDF, setExportingPDF] = useState(false);
  const [socialMedia, setSocialMedia] = useState<{
    profiles: Record<string, string | null>;
    health_analysis?: any;
    marketing_potential?: string;
    overall_score?: number;
  } | null>(null);
  const [loadingSocialMedia, setLoadingSocialMedia] = useState(false);
  const [reviewAnalysis, setReviewAnalysis] = useState<{
    score?: number;
    sentiment?: any;
    positive_themes?: string[];
    negative_themes?: string[];
    gaps?: string[];
    opportunities?: Array<{
      priority: string;
      category: string;
      title: string;
      description: string;
      impact?: string;
    }>;
  } | null>(null);
  const [loadingReviewAnalysis, setLoadingReviewAnalysis] = useState(false);
  const [aeogeoAnalysis, setAeogeoAnalysis] = useState<{
    score?: number;
    visibility_factors?: string[];
    recommendations?: string[];
    summary?: string;
    ai_citation_readiness?: string;
  } | null>(null);
  const [loadingAeogeo, setLoadingAeogeo] = useState(false);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  useEffect(() => {
    if (id) {
      fetchBusinessDetails();
      fetchSocialMedia(false); // Fetch social media profiles without analysis initially
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch fns depend on id
  }, [id]);

  useEffect(() => {
    if (id && usage?.analysis_features?.includes('website')) {
      fetchAnalysis();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch fns depend on id, usage
  }, [id, usage?.analysis_features]);

  const fetchBusinessDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await api.get(`/business/${id}`);
      setBusiness(response.data);
    } catch (err) {
      console.error('Error fetching business details:', err);
      alert('Error loading business details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysis = async () => {
    if (!id) return;
    try {
      const response = await api.get(`/analysis/website/${id}`);
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Error fetching analysis:', err);
      if (err?.response?.status === 403) {
        setUpgradeMessage(err?.response?.data?.detail || 'Upgrade for Website analysis.');
        setShowUpgrade(true);
      }
    }
  };

  const fetchLocalSEO = async () => {
    if (!id) return;
    setLoadingLocalSEO(true);
    try {
      const response = await api.get(`/analysis/local-seo/${id}`);
      setLocalSEO(response.data);
    } catch (err: any) {
      console.error('Error fetching Local SEO analysis:', err);
      if (err?.response?.status === 403) {
        setUpgradeMessage(err?.response?.data?.detail || 'Upgrade for Local SEO analysis.');
        setShowUpgrade(true);
      }
    } finally {
      setLoadingLocalSEO(false);
    }
  };

  const fetchSocialMedia = async (analyze: boolean = false) => {
    if (!id) return;
    setLoadingSocialMedia(true);
    try {
      const response = await api.get(`/social/${id}?analyze=${analyze}`);
      setSocialMedia(response.data);
    } catch (err: any) {
      console.error('Error fetching social media:', err);
      if (err?.response?.status === 403) {
        setUpgradeMessage(err?.response?.data?.detail || 'Upgrade to Basic for Social Media analysis.');
        setShowUpgrade(true);
      }
    } finally {
      setLoadingSocialMedia(false);
    }
  };

  const fetchReviewAnalysis = async () => {
    if (!id) return;
    setLoadingReviewAnalysis(true);
    try {
      const response = await api.get(`/reviews/${id}`);
      setReviewAnalysis(response.data);
    } catch (err: any) {
      console.error('Error fetching review analysis:', err);
      if (err?.response?.status === 403) {
        setUpgradeMessage(err?.response?.data?.detail || 'Upgrade to Basic for Review analysis.');
        setShowUpgrade(true);
      }
    } finally {
      setLoadingReviewAnalysis(false);
    }
  };

  const fetchAeogeoAnalysis = async () => {
    if (!id) return;
    setLoadingAeogeo(true);
    try {
      const response = await api.get(`/analysis/aeogeo/${id}`);
      setAeogeoAnalysis(response.data);
    } catch (err: any) {
      console.error('Error fetching AEO/GEO analysis:', err);
      if (err?.response?.status === 403) {
        setUpgradeMessage(err?.response?.data?.detail || 'Upgrade to Pro for AEO/GEO (LLM) analysis.');
        setShowUpgrade(true);
      }
    } finally {
      setLoadingAeogeo(false);
    }
  };

  const handleSaveLead = async () => {
    if (!id) return;
    setSavingLead(true);
    try {
      await api.post('/leads', {
        business_profile_id: parseInt(id),
        status: 'New',
      });
      alert('Lead saved successfully!');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Error saving lead';
      if (err.response?.status === 403 && errorMessage.includes('Upgrade')) {
        setUpgradeMessage(errorMessage);
        setShowUpgrade(true);
      } else {
        alert(errorMessage);
      }
    } finally {
      setSavingLead(false);
    }
  };

  const handleGeneratePitch = async () => {
    if (!id) return;
    setGeneratingPitch(true);
    try {
      const response = await api.post('/pitches/generate', {
        business_id: parseInt(id),
        tone: 'friendly',
        format: 'email',
      });
      setPitch(response.data.pitch_text);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error generating pitch');
    } finally {
      setGeneratingPitch(false);
    }
  };

  const handleBack = () => {
    // If we came from search, go back to search, otherwise go to dashboard
    if (hasSearched) {
      navigate('/search');
    } else {
      navigate('/dashboard');
    }
  };

  const handleExportPDF = async () => {
    if (!id) return;
    setExportingPDF(true);
    try {
      const response = await api.get(`/export/business/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `business_report_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error exporting PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header activeTab="intelligence" />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-12">
        <BackToDashboard />
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exportingPDF}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-all disabled:bg-slate-300"
          >
            {exportingPDF ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export PDF
              </>
            )}
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-slate-100 rounded w-1/3"></div>
              <div className="h-4 bg-slate-50 rounded w-1/2"></div>
            </div>
          </div>
        ) : business ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Business Info Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-3xl font-black text-slate-900 mb-2">{business.name}</h2>
                    {business.address && (
                      <div className="flex items-center gap-2 text-slate-600 mb-1">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{business.address}</span>
                      </div>
                    )}
                    {business.rating && (
                      <div className="flex items-center gap-2">
                        <span className="text-yellow-500 text-xl">⭐</span>
                        <span className="text-xl font-black text-slate-900">{business.rating}</span>
                        <span className="text-slate-500">({business.review_count} reviews)</span>
                      </div>
                    )}
                  </div>
                  <div className="bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100">
                    <div className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Potential</div>
                    <div className="text-2xl font-black text-emerald-600">
                      {business.has_website ? '60%' : '95%'}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button 
                    onClick={handleSaveLead} 
                    disabled={savingLead}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-all disabled:bg-slate-300 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    {savingLead ? 'Saving...' : 'Save Lead'}
                  </button>
                  <button 
                    onClick={handleGeneratePitch} 
                    disabled={generatingPitch}
                    className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition-all disabled:bg-slate-300"
                  >
                    {generatingPitch ? 'Generating...' : 'Generate Pitch'}
                  </button>
                  {hasAnalysisFeature('local_seo') && (
                    <button 
                      onClick={fetchLocalSEO} 
                      disabled={loadingLocalSEO}
                      className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 transition-all disabled:bg-slate-300"
                    >
                      {loadingLocalSEO ? 'Analyzing...' : 'Analyze Local SEO'}
                    </button>
                  )}
                </div>
              </div>

              {/* SEO Analysis Card - Free+ */}
              {hasAnalysisFeature('website') && analysis && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-4">SEO & Performance Analysis</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-bold text-slate-600">SEO Score</span>
                        <span className="text-2xl font-black text-indigo-600">{analysis.seo_score}/100</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-indigo-600 h-2 rounded-full transition-all"
                          style={{ width: `${analysis.seo_score}%` }}
                        ></div>
                      </div>
                    </div>
                    {analysis.summary && (
                      <p className="text-sm text-slate-600">{analysis.summary}</p>
                    )}
                    {analysis.issues.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-2">Issues Found:</h4>
                        <ul className="space-y-1">
                          {analysis.issues.map((issue, idx) => (
                            <li key={idx} className="text-sm text-rose-600">• {issue.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {analysis.recommendations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-2">Recommendations:</h4>
                        <ul className="space-y-1">
                          {analysis.recommendations.map((rec, idx) => (
                            <li key={idx} className="text-sm text-emerald-600">✓ {rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Local SEO Analysis - Free+ */}
              {hasAnalysisFeature('local_seo') && localSEO && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-4">Local SEO Analysis</h3>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <div className="text-sm font-bold text-slate-600 mb-1">Overall Score</div>
                      <div className="text-3xl font-black text-indigo-600">{localSEO.overall_score.toFixed(1)}/100</div>
                    </div>
                    {localSEO.opportunity_score !== undefined && (
                      <div>
                        <div className="text-sm font-bold text-slate-600 mb-1">Opportunity Score</div>
                        <div className="text-3xl font-black text-emerald-600">{localSEO.opportunity_score.toFixed(1)}/100</div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    {localSEO.scores.gbp !== undefined && (
                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <div className="text-xs font-bold text-slate-400 uppercase mb-1">GBP</div>
                        <div className="text-xl font-black text-slate-900">{localSEO.scores.gbp.toFixed(0)}</div>
                      </div>
                    )}
                    {localSEO.scores.website !== undefined && (
                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <div className="text-xs font-bold text-slate-400 uppercase mb-1">Website</div>
                        <div className="text-xl font-black text-slate-900">{localSEO.scores.website.toFixed(0)}</div>
                      </div>
                    )}
                    {localSEO.scores.reviews !== undefined && (
                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <div className="text-xs font-bold text-slate-400 uppercase mb-1">Reviews</div>
                        <div className="text-xl font-black text-slate-900">{localSEO.scores.reviews.toFixed(0)}</div>
                      </div>
                    )}
                    {localSEO.scores.citations !== undefined && (
                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <div className="text-xs font-bold text-slate-400 uppercase mb-1">Citations</div>
                        <div className="text-xl font-black text-slate-900">{localSEO.scores.citations.toFixed(0)}</div>
                      </div>
                    )}
                    {localSEO.scores.relevance !== undefined && (
                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <div className="text-xs font-bold text-slate-400 uppercase mb-1">Relevance</div>
                        <div className="text-xl font-black text-slate-900">{localSEO.scores.relevance.toFixed(0)}</div>
                      </div>
                    )}
                  </div>
                  {localSEO.summary && (
                    <p className="text-sm text-slate-600 mb-4">{localSEO.summary}</p>
                  )}
                  {localSEO.issues.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-bold text-slate-900 mb-2">Issues:</h4>
                      <ul className="space-y-1">
                        {localSEO.issues.slice(0, 5).map((issue, idx) => (
                          <li key={idx} className="text-sm text-rose-600">• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {localSEO.recommendations.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 mb-2">Recommendations:</h4>
                      <ul className="space-y-1">
                        {localSEO.recommendations.slice(0, 5).map((rec, idx) => (
                          <li key={idx} className="text-sm text-emerald-600">✓ {rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Social Media Analysis - Basic+ */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-black text-slate-900">Social Media Presence</h3>
                  {hasAnalysisFeature('social') ? (
                    <button
                      onClick={() => fetchSocialMedia(true)}
                      disabled={loadingSocialMedia}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-all disabled:bg-slate-300 flex items-center gap-2"
                    >
                    {loadingSocialMedia ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Analyze Social Media
                      </>
                    )}
                  </button>
                  ) : (
                    <button
                      onClick={() => {
                        setUpgradeMessage('Upgrade to Basic for Social Media analysis.');
                        setShowUpgrade(true);
                      }}
                      className="px-4 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm font-bold hover:bg-slate-300 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Upgrade for Social Analysis
                    </button>
                  )}
                </div>

                {loadingSocialMedia && !socialMedia ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
                    <p className="mt-4 text-sm text-slate-600">Analyzing social media...</p>
                  </div>
                ) : socialMedia ? (
                  <div className="space-y-4">
                    {/* Social Media Handles */}
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 mb-3">Platforms Found:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {['facebook', 'instagram', 'twitter', 'linkedin', 'youtube'].map((platform) => {
                          const handle = socialMedia.profiles?.[platform];
                          const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
                          return (
                            <div key={platform} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-slate-700">{platformName}:</span>
                                {handle ? (
                                  <a
                                    href={handle.startsWith('http') ? handle : `https://${handle}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-indigo-600 hover:underline font-medium truncate max-w-[200px]"
                                  >
                                    {handle.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                                  </a>
                                ) : (
                                  <span className="text-sm text-slate-400 italic">Not Found</span>
                                )}
                              </div>
                              {handle && (
                                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Analysis Results */}
                    {socialMedia.health_analysis && (
                      <div className="mt-6 pt-6 border-t border-slate-200">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <div className="text-sm font-bold text-slate-600 mb-1">Overall Score</div>
                            <div className="text-2xl font-black text-indigo-600">
                              {socialMedia.overall_score || socialMedia.health_analysis.overall_score || 0}/100
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-slate-600 mb-1">Marketing Potential</div>
                            <div className={`text-2xl font-black ${
                              (socialMedia.marketing_potential || socialMedia.health_analysis.marketing_potential) === 'High' 
                                ? 'text-emerald-600' 
                                : (socialMedia.marketing_potential || socialMedia.health_analysis.marketing_potential) === 'Medium'
                                ? 'text-amber-600'
                                : 'text-slate-600'
                            }`}>
                              {socialMedia.marketing_potential || socialMedia.health_analysis.marketing_potential || 'Unknown'}
                            </div>
                          </div>
                        </div>
                        {socialMedia.health_analysis.recommendations && socialMedia.health_analysis.recommendations.length > 0 && (
                          <div>
                            <h4 className="text-sm font-bold text-slate-900 mb-2">Recommendations:</h4>
                            <ul className="space-y-1">
                              {socialMedia.health_analysis.recommendations.map((rec: string, idx: number) => (
                                <li key={idx} className="text-sm text-emerald-600">✓ {rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {socialMedia.health_analysis.summary && (
                          <p className="text-sm text-slate-600 mt-4">{socialMedia.health_analysis.summary}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-500 mb-4">Click "Analyze Social Media" to discover social media profiles and get health analysis.</p>
                  </div>
                )}
              </div>

              {/* Review Analysis - Basic+ */}
              {business && business.review_count > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-black text-slate-900">Review Analysis</h3>
                    {hasAnalysisFeature('reviews') ? (
                    <button
                      onClick={fetchReviewAnalysis}
                      disabled={loadingReviewAnalysis}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-all disabled:bg-slate-300 flex items-center gap-2"
                    >
                      {loadingReviewAnalysis ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                          Analyze Reviews
                        </>
                      )}
                    </button>
                    ) : (
                      <button
                        onClick={() => {
                          setUpgradeMessage('Upgrade to Basic for Review analysis.');
                          setShowUpgrade(true);
                        }}
                        className="px-4 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm font-bold hover:bg-slate-300 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Upgrade for Review Analysis
                      </button>
                    )}
                  </div>

                  {hasAnalysisFeature('reviews') && loadingReviewAnalysis && !reviewAnalysis ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
                      <p className="mt-4 text-sm text-slate-600">Analyzing reviews for pain points and opportunities...</p>
                    </div>
                  ) : hasAnalysisFeature('reviews') && reviewAnalysis ? (
                    <div className="space-y-6">
                      {/* Score and Sentiment */}
                      {reviewAnalysis.score !== undefined && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm font-bold text-slate-600 mb-1">Review Score</div>
                            <div className="text-2xl font-black text-indigo-600">{reviewAnalysis.score.toFixed(1)}/100</div>
                          </div>
                          {reviewAnalysis.sentiment && (
                            <div>
                              <div className="text-sm font-bold text-slate-600 mb-1">Overall Sentiment</div>
                              <div className={`text-2xl font-black ${
                                reviewAnalysis.sentiment.overall === 'positive' 
                                  ? 'text-emerald-600' 
                                  : reviewAnalysis.sentiment.overall === 'negative'
                                  ? 'text-rose-600'
                                  : 'text-amber-600'
                              }`}>
                                {reviewAnalysis.sentiment.overall?.charAt(0).toUpperCase() + reviewAnalysis.sentiment.overall?.slice(1) || 'Neutral'}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Pain Points / Negative Themes - Most Important for Lead Generation */}
                      {reviewAnalysis.negative_themes && reviewAnalysis.negative_themes.length > 0 && (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                          <h4 className="text-sm font-black text-rose-900 mb-3 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Pain Points & Issues (Lead Opportunities)
                          </h4>
                          <ul className="space-y-2">
                            {reviewAnalysis.negative_themes.map((theme: unknown, idx: number) => (
                              <li key={idx} className="text-sm text-rose-700 font-medium">• {themeToStr(theme)}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Opportunities */}
                      {reviewAnalysis.opportunities && reviewAnalysis.opportunities.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-slate-900 mb-3">Opportunities (Prioritized):</h4>
                          <div className="space-y-2">
                            {reviewAnalysis.opportunities
                              .sort((a, b) => {
                                const priorityOrder: Record<string, number> = { high: 3, medium: 2, low: 1 };
                                return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
                              })
                              .map((opp, idx) => (
                                <div
                                  key={idx}
                                  className={`p-3 rounded-lg border ${
                                    opp.priority === 'high'
                                      ? 'bg-amber-50 border-amber-200'
                                      : opp.priority === 'medium'
                                      ? 'bg-blue-50 border-blue-200'
                                      : 'bg-slate-50 border-slate-200'
                                  }`}
                                >
                                  <div className="flex items-start justify-between mb-1">
                                    <span className={`text-xs font-black uppercase tracking-wider ${
                                      opp.priority === 'high'
                                        ? 'text-amber-700'
                                        : opp.priority === 'medium'
                                        ? 'text-blue-700'
                                        : 'text-slate-700'
                                    }`}>
                                      {opp.priority} Priority
                                    </span>
                                    <span className="text-xs font-bold text-slate-500">{opp.category}</span>
                                  </div>
                                  <div className="text-sm font-bold text-slate-900 mb-1">{opp.title}</div>
                                  <div className="text-xs text-slate-600">{opp.description}</div>
                                  {opp.impact && (
                                    <div className="text-xs text-slate-500 mt-1 italic">Impact: {opp.impact}</div>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Positive Themes */}
                      {reviewAnalysis.positive_themes && reviewAnalysis.positive_themes.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-slate-900 mb-2">Positive Themes:</h4>
                          <ul className="space-y-1">
                            {reviewAnalysis.positive_themes.map((theme: unknown, idx: number) => (
                              <li key={idx} className="text-sm text-emerald-600">✓ {themeToStr(theme)}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Gaps */}
                      {reviewAnalysis.gaps && reviewAnalysis.gaps.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-slate-900 mb-2">Identified Gaps:</h4>
                          <ul className="space-y-1">
                            {reviewAnalysis.gaps.map((gap: string, idx: number) => (
                              <li key={idx} className="text-sm text-slate-600">• {gapToLabel(gap)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : hasAnalysisFeature('reviews') ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-slate-500 mb-4">
                        This business has {business.review_count} review{business.review_count !== 1 ? 's' : ''} on Google.
                      </p>
                      <p className="text-sm text-slate-500">Click "Analyze Reviews" to identify pain points and lead opportunities.</p>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-sm text-slate-600 mb-2">Review analysis is a Basic plan feature.</p>
                      <p className="text-sm text-slate-500 mb-4">Upgrade to analyze sentiment, pain points, and lead opportunities.</p>
                      <button
                        onClick={() => {
                          setUpgradeMessage('Upgrade to Basic for Review analysis.');
                          setShowUpgrade(true);
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700"
                      >
                        Upgrade to Basic
                      </button>
                    </div>
                  )}
                </div>
              )}

              {business && business.review_count === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-4">Review Analysis</h3>
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-500">No reviews available for this business.</p>
                  </div>
                </div>
              )}

              {/* AEO/GEO Analysis - Pro/Enterprise only */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-black text-slate-900">
                    AEO/GEO Analysis
                    <span className="ml-2 text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Pro</span>
                  </h3>
                  {hasAnalysisFeature('aeogeo') ? (
                    <button
                      onClick={fetchAeogeoAnalysis}
                      disabled={loadingAeogeo}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-all disabled:bg-slate-300 flex items-center gap-2"
                    >
                      {loadingAeogeo ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          Analyze AI Visibility
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setUpgradeMessage('Upgrade to Pro for AEO/GEO (AI/LLM search visibility) analysis.');
                        setShowUpgrade(true);
                      }}
                      className="px-4 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm font-bold hover:bg-slate-300 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Pro Feature
                    </button>
                  )}
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Analyze how well this business is optimized for AI search (ChatGPT, Perplexity, Google AI Overview).
                </p>
                {hasAnalysisFeature('aeogeo') && loadingAeogeo && !aeogeoAnalysis ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
                    <p className="mt-4 text-sm text-slate-600">Analyzing AI visibility...</p>
                  </div>
                ) : hasAnalysisFeature('aeogeo') && aeogeoAnalysis ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-bold text-slate-600 mb-1">AEO/GEO Score</div>
                        <div className="text-2xl font-black text-indigo-600">{aeogeoAnalysis.score?.toFixed(0) ?? 0}/100</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-600 mb-1">AI Citation Readiness</div>
                        <div className={`text-lg font-bold ${
                          aeogeoAnalysis.ai_citation_readiness === 'high' ? 'text-emerald-600' :
                          aeogeoAnalysis.ai_citation_readiness === 'medium' ? 'text-amber-600' : 'text-slate-600'
                        }`}>
                          {aeogeoAnalysis.ai_citation_readiness?.charAt(0).toUpperCase()}{aeogeoAnalysis.ai_citation_readiness?.slice(1) ?? 'Unknown'}
                        </div>
                      </div>
                    </div>
                    {aeogeoAnalysis.summary && (
                      <p className="text-sm text-slate-600">{aeogeoAnalysis.summary}</p>
                    )}
                    {aeogeoAnalysis.recommendations && aeogeoAnalysis.recommendations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-2">Recommendations:</h4>
                        <ul className="space-y-1">
                          {aeogeoAnalysis.recommendations.map((rec: string, idx: number) => (
                            <li key={idx} className="text-sm text-emerald-600">✓ {rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {aeogeoAnalysis.visibility_factors && aeogeoAnalysis.visibility_factors.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-2">Visibility Factors:</h4>
                        <ul className="space-y-1">
                          {aeogeoAnalysis.visibility_factors.map((f: string, idx: number) => (
                            <li key={idx} className="text-sm text-slate-600">• {f}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : !hasAnalysisFeature('aeogeo') ? (
                  <div className="text-center py-6 bg-indigo-50 rounded-xl border border-indigo-100">
                    <p className="text-sm text-slate-600 mb-2">AEO/GEO analysis optimizes for AI search visibility.</p>
                    <p className="text-sm text-slate-500 mb-4">Upgrade to Pro to analyze how AI assistants find and cite this business.</p>
                    <button
                      onClick={() => {
                        setUpgradeMessage('Upgrade to Pro for AEO/GEO (LLM) analysis.');
                        setShowUpgrade(true);
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700"
                    >
                      Upgrade to Pro
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-500">Click "Analyze AI Visibility" to run AEO/GEO analysis.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Contact & Pitch */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-black text-slate-900 mb-4">Contact Information</h3>
                {business.phone && (
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="font-medium">{business.phone}</span>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(business.phone || '');
                        alert('Phone number copied!');
                      }}
                      className="text-indigo-600 hover:text-indigo-700"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                )}
                {business.website && (
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9h18" />
                    </svg>
                    <a 
                      href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 font-bold hover:underline"
                    >
                      {business.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>

              {/* Personalized Pitch */}
              {pitch && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-black text-slate-900">Personalized Pitch</h3>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(pitch);
                        alert('Pitch copied to clipboard!');
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy Pitch
                    </button>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
                    {pitch}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <p className="text-slate-600">Business not found</p>
          </div>
        )}

        <UpgradePrompt
          message={upgradeMessage}
          show={showUpgrade}
          onClose={() => setShowUpgrade(false)}
        />
      </main>
    </div>
  );
};

export default BusinessDetail;
